import { Loader2, Upload } from "lucide-react";
import { useState } from "react";

import { UploadM1Dialog } from "@/components/dashboard/UploadM1Dialog";
import { Button } from "@/components/ui/button";
import { useStatoDati } from "@/hooks/useDashboard";
import { cn } from "@/lib/utils";
import type {
  FileMonitoratoStato,
  SemaforoFreschezza,
} from "@/types/api";

const COLORI: Record<SemaforoFreschezza, string> = {
  verde:
    "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800",
  giallo:
    "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  rosso:
    "bg-red-100 text-red-900 border-red-300 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800",
};

function formatEta(eta_ore: number | null): string {
  if (eta_ore === null) return "mai";
  if (eta_ore < 1) return "<1h";
  if (eta_ore < 24) return `${Math.round(eta_ore)}h`;
  return `${Math.round(eta_ore / 24)}g`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "mai caricato";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WidgetStatoDati() {
  const { data, isLoading } = useStatoDati();
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {isLoading ? (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          ) : (
            data?.files.map((f) => <FileChip key={f.tipo_file} file={f} />)
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="default"
          onClick={() => setUploadOpen(true)}
        >
          <Upload className="size-4" />
          Aggiorna dati
        </Button>
      </div>

      {data?.ultimo_caricamento_globale_il && (
        <p className="text-xs text-muted-foreground">
          Ultimo upload:{" "}
          <span className="text-foreground">
            {data.ultimo_caricamento_globale_tipo}
          </span>{" "}
          · {formatTimestamp(data.ultimo_caricamento_globale_il)}
          {data.ultimo_caricamento_globale_da_email &&
            ` · ${data.ultimo_caricamento_globale_da_email}`}
        </p>
      )}

      {uploadOpen && <UploadM1Dialog onClose={() => setUploadOpen(false)} />}
    </div>
  );
}

function FileChip({ file }: { file: FileMonitoratoStato }) {
  const colore = COLORI[file.semaforo];
  return (
    <div
      className={cn(
        "rounded-md border px-2.5 py-1 text-xs tabular-nums flex items-center gap-1.5",
        colore
      )}
      title={`${file.titolo_lungo} · ${formatTimestamp(file.ultimo_caricamento_il)}${
        file.n_righe_ok ? ` · ${file.n_righe_ok} righe` : ""
      }`}
    >
      <span className="font-semibold">{file.titolo_breve}</span>
      <span className="opacity-70">{formatEta(file.eta_ore)}</span>
    </div>
  );
}
